// Request quotation: onboarding condicionado a login o creación de cuenta.
// Logueado: solo datos de la cotización (incoterm, puerto, especificaciones).
// No logueado: "¿Tiene cuenta?" Si → login; No → wizard (datos usuario → datos request → enviar + código → revisa tu email).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuotationCart } from '@/hooks/use-quotation-cart';
import { useSubmitQuotation, useSourcingItems, useClientLead } from '@/hooks/use-store';
import { postWorkerApi } from '@/lib/worker-api';
import { StoreBreadcrumbs } from '@/components/store/StoreBreadcrumbs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { reportCaughtError } from '@/lib/report-error';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { StoreThumbnailImg } from '@/components/store/StoreThumbnailImg';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, Zap, Package, TrendingUp, CheckCircle2, LogIn, UserPlus, Mail, ShieldCheck } from 'lucide-react';

// Logged-in: only request data
const schemaRequestOnly = z.object({
  specifications: z.string().optional(),
  incoterm: z.enum(['CIF', 'DDP']),
  destination_port_or_address: z.string().min(1, 'Indique puerto o dirección de destino'),
});
type FormRequestOnly = z.infer<typeof schemaRequestOnly>;

// Wizard step 1: user data. Un solo teléfono con código de país; checkbox indica si tiene WhatsApp.
const schemaUser = z.object({
  name: z.string().min(1, 'Nombre requerido'),
  email: z.string().email('Email no válido'),
  phone: z.string().optional(),
  phoneHasWhatsapp: z.boolean().optional(),
  company: z.string().optional(),
  country: z.string().optional(),
  city: z.string().optional(),
});
type FormUser = z.infer<typeof schemaUser>;

// Wizard step 2: request data
const schemaWizardRequest = z.object({
  specifications: z.string().optional(),
  incoterm: z.enum(['CIF', 'DDP']),
  destination_port_or_address: z.string().min(1, 'Indique puerto o dirección de destino'),
});
type FormWizardRequest = z.infer<typeof schemaWizardRequest>;

type OnboardingStep = 'question' | 'user' | 'request' | 'check_email';

const StoreCartRequest = () => {
  const navigate = useNavigate();
  const { items, requestId, request } = useQuotationCart();
  const { data: allItems } = useSourcingItems(null);
  const { data: clientLead } = useClientLead();
  const submitQuotation = useSubmitQuotation();
  const { toast } = useToast();

  const isLoggedIn = !!clientLead;

  // Non-logged-in: "¿Tiene cuenta?" then wizard (Sí → login; No → user form, no extra button).
  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>('question');
  const [wizardUserData, setWizardUserData] = useState<FormUser | null>(null);
  const [wizardEmail, setWizardEmail] = useState<string>('');

  const formRequestOnly = useForm<FormRequestOnly>({
    resolver: zodResolver(schemaRequestOnly),
    defaultValues: { specifications: '', incoterm: 'CIF', destination_port_or_address: '' },
  });

  // Prefill form once when request has saved destination/specs (e.g. "Editar y reenviar" from pendientes).
  const lastPreloadedRequestId = useRef<string | null>(null);
  useEffect(() => {
    if (!request?.id || request.id !== requestId) return;
    if (lastPreloadedRequestId.current === request.id) return;
    const port = request.destination_port_or_address ?? '';
    const specs = request.specifications ?? '';
    const incoterm = (request.incoterm === 'DDP' ? 'DDP' : 'CIF') as 'CIF' | 'DDP';
    if (port || specs || request.incoterm) {
      formRequestOnly.reset({ destination_port_or_address: port, specifications: specs, incoterm });
      lastPreloadedRequestId.current = request.id;
    }
  }, [request?.id, requestId, request?.destination_port_or_address, request?.specifications, request?.incoterm, formRequestOnly]);
  useEffect(() => {
    if (requestId !== lastPreloadedRequestId.current) lastPreloadedRequestId.current = null;
  }, [requestId]);

  const formUser = useForm<FormUser>({
    resolver: zodResolver(schemaUser),
    defaultValues: { name: '', email: '', phone: '', phoneHasWhatsapp: false, company: '', country: '', city: '' },
  });

  const formWizardRequest = useForm<FormWizardRequest>({
    resolver: zodResolver(schemaWizardRequest),
    defaultValues: { specifications: '', incoterm: 'CIF', destination_port_or_address: '' },
  });

  const breadcrumbs = [
    { label: 'Store', path: '/store' },
    { label: 'Orden', path: '/store/cart' },
    { label: 'Solicitar cotización' },
  ];

  // --- Logged-in: submit with lead_id only ---
  const onSubmitLoggedIn = async (values: FormRequestOnly) => {
    if (!requestId || !clientLead?.id) return;
    if (items.length === 0) {
      toast({ title: 'Orden vacía', description: 'Añade productos antes de solicitar cotización.', variant: 'destructive' });
      return;
    }
    try {
      await submitQuotation.mutateAsync({
        request_id: requestId,
        lead_id: clientLead.id,
        specifications: values.specifications ?? undefined,
        incoterm: values.incoterm,
        destination_port_or_address: values.destination_port_or_address,
      });
      toast({
        title: 'Solicitud enviada',
        description: 'El equipo está revisando tu cotización. Te notificaremos cuando esté lista.',
      });
      window.dispatchEvent(new Event('quotation-cart-cleared'));
      navigate('/store/quotations', { replace: true });
    } catch (e) {
      reportCaughtError(e, { screen: 'store_cart_request', action: 'submit_quotation', request_id: requestId });
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al enviar', variant: 'destructive' });
    }
  };

  // --- Wizard step 1: next -> save user data and go to step 2 ---
  const onUserNext = (values: FormUser) => {
    setWizardUserData(values);
    setOnboardingStep('request');
  };

  // --- Wizard step 2: submit + send verification code -> step 3 ---
  const onWizardRequestSubmit = async (values: FormWizardRequest) => {
    if (!requestId || !wizardUserData) return;
    if (items.length === 0) {
      toast({ title: 'Orden vacía', description: 'Añade productos antes de solicitar cotización.', variant: 'destructive' });
      return;
    }
    try {
      const submitRes = await postWorkerApi('/api/quotation-request/submit', {
        request_id: requestId,
        name: wizardUserData.name,
        email: wizardUserData.email,
        phone: wizardUserData.phone ?? null,
        whatsapp: wizardUserData.phoneHasWhatsapp && wizardUserData.phone?.trim() ? wizardUserData.phone.trim() : null,
        company: wizardUserData.company ?? null,
        country: wizardUserData.country ?? null,
        city: wizardUserData.city ?? null,
        specifications: values.specifications ?? null,
        incoterm: values.incoterm,
        destination_port_or_address: values.destination_port_or_address,
      });
      if (!submitRes.ok) throw new Error(submitRes.error ?? 'Error al enviar');

      const codeRes = await postWorkerApi('/api/send-verification-code', {
        email: wizardUserData.email,
        name: wizardUserData.name,
      });
      if (!codeRes.ok) {
        toast({ title: 'Solicitud enviada', description: 'No se pudo enviar el correo de verificación. Contacte con nosotros.', variant: 'destructive' });
      }

      setWizardEmail(wizardUserData.email);
      setOnboardingStep('check_email');
      // No limpiar el carrito aquí: el usuario debe seguir viendo "Tu solicitud" con los productos hasta que verifique el email.
    } catch (e) {
      reportCaughtError(e, { screen: 'store_cart_request', action: 'submit_quotation_wizard', request_id: requestId });
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al enviar', variant: 'destructive' });
    }
  };

  // Shared: order summary and benefits (sidebar content)
  const cartItemsDetailed = useMemo(() => {
    if (!allItems?.length) return items.map((i) => ({ ...i, packshot_image: null }));
    const bySlug = new Map(allItems.map((i) => [i.slug, i]));
    return items.map((i) => ({ ...i, packshot_image: bySlug.get(i.slug)?.packshot_image ?? null }));
  }, [allItems, items]);

  const productionItems = useMemo(() => {
    const hash = (v: string) => { let h = 0; for (let i = 0; i < v.length; i++) h = (h * 31 + v.charCodeAt(i)) % 1000; return h; };
    return cartItemsDetailed.filter((i) => hash(i.slug) % 3 === 0);
  }, [cartItemsDetailed]);

  const sidebarOrder = (
    <div className="rounded-xl border-2 border-primary/20 bg-white overflow-hidden shadow-sm h-fit sticky top-4">
      <div className="bg-primary px-4 py-3 text-primary-foreground">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <h2 className="text-sm font-bold">Tu solicitud</h2>
        </div>
        <p className="text-xs opacity-90 mt-0.5">
          {items.length} {items.length === 1 ? 'producto' : 'productos'} · {items.reduce((acc, i) => acc + i.quantity, 0).toLocaleString()} uds
        </p>
      </div>
      <div className="p-3 max-h-[60vh] overflow-y-auto">
        <div className="space-y-2">
          {cartItemsDetailed.map((item) => {
            const isProduction = productionItems.some((p) => p.slug === item.slug);
            return (
              <div key={item.slug} className={`flex items-start gap-2 rounded-lg p-2 ${isProduction ? 'bg-primary/5 border border-primary/20' : 'bg-zinc-50'}`}>
                <div className="shrink-0 h-10 w-10 rounded border border-zinc-200 bg-white flex items-center justify-center overflow-hidden">
                  {item.packshot_image ? (
                    <StoreThumbnailImg coverUrl={item.packshot_image} size={{ width: 40, height: 40 }} className="max-h-full max-w-full object-contain" />
                  ) : (
                    <Package className="h-4 w-4 text-zinc-300" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-zinc-900 line-clamp-1">{item.name}</p>
                  {item.salesFormat && <p className="text-[10px] text-zinc-500 truncate">{item.salesFormat}</p>}
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span className="text-[10px] text-zinc-600 font-medium">{item.quantity.toLocaleString()} {item.moqUnitLabel}</span>
                    {isProduction && (
                      <Badge variant="outline" className="h-4 px-1 text-[9px] border-primary/30 text-primary bg-primary/5">
                        <Zap className="h-2 w-2 mr-0.5" /> Activo
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const sidebarBenefits = (
    <div className="space-y-4 h-fit sticky top-4">
      {productionItems.length > 0 && (
        <div className="rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-white overflow-hidden shadow-md">
          <div className="bg-primary px-4 py-3 text-primary-foreground">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center shrink-0">
                <Zap className="h-4 w-4 animate-pulse" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold flex items-center gap-2">
                  Producción activa
                  <Badge className="bg-primary-foreground/20 text-primary-foreground border-0 text-[10px]">{productionItems.length}</Badge>
                </h3>
              </div>
            </div>
          </div>
          <div className="p-3">
            <p className="text-xs text-zinc-700 mb-3">
              Estos productos están siendo fabricados. <span className="font-semibold text-primary">Reserva tu cupo</span> para el próximo batch.
            </p>
            <div className="space-y-1.5">
              {productionItems.map((item) => (
                <div key={item.slug} className="flex items-center gap-2 rounded-lg bg-white p-2 border border-primary/10">
                  <div className="shrink-0 h-8 w-8 rounded border border-primary/20 bg-primary/5 flex items-center justify-center overflow-hidden">
                    {item.packshot_image ? (
                      <StoreThumbnailImg coverUrl={item.packshot_image} size={{ width: 32, height: 32 }} className="max-h-full max-w-full object-contain" />
                    ) : (
                      <Package className="h-3 w-3 text-primary/50" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium text-zinc-900 line-clamp-1">{item.name}</p>
                    <div className="flex items-center gap-1">
                      <Clock className="h-2.5 w-2.5 text-primary" />
                      <span className="text-[9px] text-primary font-semibold">En producción</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="rounded-xl border-2 border-primary/20 bg-white overflow-hidden">
        <div className="bg-primary/10 px-4 py-3 border-b border-primary/10">
          <h3 className="text-sm font-bold text-primary flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            ¿Por qué elegirnos?
          </h3>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <CheckCircle2 className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold text-zinc-900">Cotización personalizada</p>
              <p className="text-[11px] text-zinc-500">Adaptada a tus necesidades</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Clock className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold text-zinc-900">Respuesta rápida</p>
              <p className="text-[11px] text-zinc-500">En 24-48 horas laborables</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Package className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold text-zinc-900">Logística optimizada</p>
              <p className="text-[11px] text-zinc-500">Mejor precio por contenedor</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Empty cart — but not when we just submitted the wizard (check_email = thank-you screen)
  if (items.length === 0 && !submitQuotation.isPending && onboardingStep !== 'check_email') {
    return (
      <div className="space-y-4">
        <StoreBreadcrumbs segments={breadcrumbs} />
        <p className="text-zinc-600">La orden está vacía.</p>
        <Button asChild variant="outline">
          <Link to="/store">Volver al store</Link>
        </Button>
      </div>
    );
  }

  const layoutGrid = (
    <div className="grid gap-6 lg:grid-cols-[1fr_280px_280px]">
      {/* Column 1: form or wizard content */}
      <Card className="border-zinc-200 bg-white">
        <CardContent className="p-6">
          {/* --- Logged-in: request-only form --- */}
          {isLoggedIn && (
            <>
              <h2 className="text-lg font-semibold text-zinc-900 mb-4">Datos de la cotización</h2>
              <Form {...formRequestOnly}>
                <form onSubmit={formRequestOnly.handleSubmit(onSubmitLoggedIn)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={formRequestOnly.control}
                      name="incoterm"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Incoterm</FormLabel>
                          <FormControl>
                            <select
                              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value as 'CIF' | 'DDP')}
                            >
                              <option value="CIF">CIF</option>
                              <option value="DDP">DDP</option>
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={formRequestOnly.control}
                      name="destination_port_or_address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Puerto o dirección de destino</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Ej. Puerto de Mariel" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={formRequestOnly.control}
                    name="specifications"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Especificaciones / comentarios</FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={3} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" disabled={submitQuotation.isPending}>
                    {submitQuotation.isPending ? 'Enviando...' : 'Enviar solicitud'}
                  </Button>
                </form>
              </Form>
            </>
          )}

          {/* --- Not logged-in: one visual step to identify; quotation not sent until done --- */}
          {!isLoggedIn && onboardingStep === 'question' && (
            <>
              <div className="rounded-xl border-2 border-[#1451af]/20 bg-[#1451af]/5 p-6 mb-8 flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-full bg-[#1451af]/15 flex items-center justify-center">
                  <ShieldCheck className="h-7 w-7 text-[#1451af]" />
                </div>
                <div>
                  <p className="text-xl font-bold text-zinc-900 tracking-tight leading-tight">
                    Un paso más y enviaremos su cotización
                  </p>
                  <p className="text-sm text-zinc-600 mt-1">Elija cómo continuar:</p>
                </div>
              </div>
              <h2 className="text-base font-semibold text-zinc-700 mb-4">¿Ya tiene cuenta?</h2>
              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="gap-2"
                  onClick={() => navigate('/store/login', { state: { from: '/store/cart/request' } })}
                >
                  <LogIn className="h-4 w-4" />
                  Sí, iniciar sesión
                </Button>
                <Button
                  type="button"
                  size="lg"
                  className="gap-2 bg-[#1451af] hover:bg-[#1451af]/90"
                  onClick={() => setOnboardingStep('user')}
                >
                  <UserPlus className="h-4 w-4" />
                  No, crear cuenta
                </Button>
              </div>
            </>
          )}

          {/* Wizard step 1: user data */}
          {!isLoggedIn && onboardingStep === 'user' && (
            <>
              <h2 className="text-lg font-semibold text-zinc-900 mb-2">Datos de contacto</h2>
              <p className="text-sm text-zinc-600 mb-4">Introduzca sus datos. En el siguiente paso indicará los datos de la cotización.</p>
              <Form {...formUser}>
                <form onSubmit={formUser.handleSubmit(onUserNext)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={formUser.control} name="name" render={({ field }) => (
                      <FormItem><FormLabel>Nombre</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={formUser.control} name="email" render={({ field }) => (
                      <FormItem><FormLabel>Correo electrónico</FormLabel><FormControl><Input type="email" {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                  </div>
                  <FormField control={formUser.control} name="phone" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Teléfono (con código de país)</FormLabel>
                      <FormControl><Input {...field} placeholder="Ej: +34 612 345 678" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={formUser.control} name="phoneHasWhatsapp" render={({ field }) => (
                    <FormItem className="flex flex-row items-center gap-2">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="!mt-0 font-normal cursor-pointer">
                        Este teléfono tiene WhatsApp para contactar rápido
                      </FormLabel>
                    </FormItem>
                  )} />
                  <FormField control={formUser.control} name="company" render={({ field }) => (
                    <FormItem><FormLabel>Empresa</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={formUser.control} name="country" render={({ field }) => (
                      <FormItem><FormLabel>País</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={formUser.control} name="city" render={({ field }) => (
                      <FormItem><FormLabel>Ciudad</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setOnboardingStep('question')}>Atrás</Button>
                    <Button type="submit">Siguiente</Button>
                  </div>
                </form>
              </Form>
            </>
          )}

          {/* Wizard step 2: request data */}
          {!isLoggedIn && onboardingStep === 'request' && wizardUserData && (
            <>
              <h2 className="text-lg font-semibold text-zinc-900 mb-2">Datos de la cotización</h2>
              <p className="text-sm text-zinc-600 mb-4">Indique incoterm, puerto de destino y comentarios. Al enviar, le mandaremos un correo para verificar su cuenta y crear su contraseña.</p>
              <Form {...formWizardRequest}>
                <form onSubmit={formWizardRequest.handleSubmit(onWizardRequestSubmit)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={formWizardRequest.control}
                      name="incoterm"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Incoterm</FormLabel>
                          <FormControl>
                            <select
                              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value as 'CIF' | 'DDP')}
                            >
                              <option value="CIF">CIF</option>
                              <option value="DDP">DDP</option>
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={formWizardRequest.control}
                      name="destination_port_or_address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Puerto o dirección de destino</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Ej. Puerto de Mariel" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={formWizardRequest.control}
                    name="specifications"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Especificaciones / comentarios</FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={3} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setOnboardingStep('user')}>Atrás</Button>
                    <Button type="submit">Enviar solicitud y crear cuenta</Button>
                  </div>
                </form>
              </Form>
            </>
          )}

          {/* Wizard step 3: thank you + check email */}
          {!isLoggedIn && onboardingStep === 'check_email' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <Mail className="h-7 w-7" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-zinc-900 text-center mb-2">Gracias por su solicitud</h2>
              <p className="text-sm text-zinc-600 text-center mb-4">
                En breves le llegará un correo para finalizar el proceso y activar su cuenta.
              </p>
              <p className="text-sm text-zinc-600 text-center mb-4">
                Hemos enviado un correo a <strong>{wizardEmail}</strong>. Abra el enlace o introduzca el código en la página de verificación para crear su contraseña.
              </p>
              <div className="flex justify-center">
                <Button asChild>
                  <Link to={`/store/newclientidentification?email=${encodeURIComponent(wizardEmail)}`}>Ir a verificación</Link>
                </Button>
              </div>
              <p className="text-xs text-zinc-500 text-center mt-4">
                Después de verificar podrá ver el estado de su solicitud en Historial de cotizaciones.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {sidebarOrder}
      {sidebarBenefits}
    </div>
  );

  return (
    <div className="space-y-6">
      <StoreBreadcrumbs segments={breadcrumbs} />
      <div>
        <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">Solicitar cotización</h1>
        <p className="mt-2 text-zinc-600">
          {isLoggedIn ? 'Indique los datos de la cotización. Le contactaremos en breve.' : 'Complete sus datos. Le contactaremos con la cotización.'}
        </p>
      </div>
      {layoutGrid}
    </div>
  );
};

export default StoreCartRequest;
